import { useEffect, useRef } from "react";
import { useAuth } from "./useAuth";
import { posthog } from "@/lib/posthog";
import { useConsentStore } from "@/lib/consent/consent-store";

/**
 * Identyfikuje zalogowanego usera w PostHogu i przypina go do grup
 * (agency / holding) — pozwala filtrować retencję i lejki per organizacja.
 * Reset przy wylogowaniu, żeby nie mieszać sesji między userami.
 */
export function usePostHog() {
  const { user, isAuthenticated } = useAuth();
  const analyticsConsent = useConsentStore((state) => state.analytics);
  const lastIdentifiedRef = useRef<string | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    // VP-312: bez zgody analitycznej PostHog nie jest zainicjalizowany — identify
    // wysłałoby dane usera do produktu, na który nikt się nie zgodził. Czyścimy też
    // ślad po identyfikacji: wycofanie zgody resetuje distinct_id, więc po ponownej
    // zgodzie musimy zidentyfikować usera od nowa, a nie wpaść w guard poniżej.
    if (!analyticsConsent) {
      lastIdentifiedRef.current = null;
      return;
    }

    if (isAuthenticated && user) {
      if (lastIdentifiedRef.current === user.id) return;

      posthog.identify(user.id, {
        email: user.email,
        name: user.username ?? user.firstName ?? null,
        role: user.role ?? null,
        agency_id: user.agencyId ?? null,
        agency_name: user.agencyName ?? null,
        plan_key: user.subscriptionPlanKey ?? null,
        subscription_active: user.subscriptionActive ?? false,
        trial_ends_at: user.trialEndsAt ?? null,
        past_due: user.pastDue ?? false,
        locale: user.locale ?? null,
        preferred_currency: user.preferredCurrency ?? null,
      });

      if (user.agencyId) {
        posthog.group("agency", user.agencyId, {
          name: user.agencyName ?? user.agencyId,
        });
      }

      lastIdentifiedRef.current = user.id;
      return;
    }

    if (!isAuthenticated && lastIdentifiedRef.current) {
      posthog.reset();
      lastIdentifiedRef.current = null;
    }
  }, [isAuthenticated, user, analyticsConsent]);

  return {
    track: posthog.capture.bind(posthog),
    identify: posthog.identify.bind(posthog),
    reset: posthog.reset.bind(posthog),
    captureException: posthog.captureException.bind(posthog),
    posthog,
  };
}
